import { type useMutation, type useLazyQuery } from '@apollo/client/react';
import loadable from '@loadable/component';
import { type Dispatch, type FunctionComponent, type SetStateAction, useContext } from 'react';
import type {
	AddProductToCartMutation,
	Exact,
	GetProductConfigurationInput,
	GetProductConfigurationQuery
} from '../../../../../__generated__/graphql-client-types';
import { AddSampleToCartButton } from '../../../../../components/product-components/add-sample-to-cart-button/add-sample-to-cart-button.component';
import { AddToCartSection } from '../../../../../components/product-components/product-controls/add-to-cart-section/add-to-cart-section.component';
import { ExtraControlsWidget } from '../../../../../components/product-components/product-controls/extra-controls-widget/extra-controls-widget.component';
import { ProductVariationGroup } from '../../../../../components/product-components/product-controls/product-variation-group/product-variation-group.component';
import { ShippingEligibility } from '../../../../../components/product-components/shipping-eligibility/shipping-eligibility.component';
import { ProductConfiguration } from '../../../../../components/product-configuration/product-configuration.component';
import { PRODUCT_CONFIG_SECTION_ID } from '../../../../../constants/product';
import { AddToCartContext } from '../../../../../contexts/add-to-cart/add-to-cart.context';
import { ProductFamilyContext } from '../../../../../contexts/product-family/product-family.context';
import { VariantContext } from '../../../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES } from '../../../../../helpers/analytics/analytics.helper';
import { type ADD_TO_CART_BUTTON_TYPE } from '../../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { mapVariationGroupsToSelectedVariant } from '../../../../../helpers/product-helper/variations/variations.helper';
import { useSiteViewPreference } from '../../../../../hooks/apollo/employee/employee.hooks';

const LoadableInternalToolsPanel = loadable(
	() =>
		import(
			/* webpackChunkName: "internalproductinformation" */ '../../../../../components/product-components/internal-tools-panel/internal-tools-panel.component'
		),
	{
		resolveComponent: ({ InternalToolsPanel }) => InternalToolsPanel
	}
);

export type ProductControlsProps = {
	onAddToCartClick: (addToCartButtonType?: ADD_TO_CART_BUTTON_TYPE) => void;
	resetAddToCart: () => void;
	addToCartStatus: useMutation.Result<AddProductToCartMutation>;
	units: {
		unitsValue: number;
		setUnits: Dispatch<SetStateAction<number>>;
	};
	status: useLazyQuery.Result<
		GetProductConfigurationQuery,
		Exact<{
			input: GetProductConfigurationInput;
		}>,
		'empty' | 'complete' | 'streaming'
	>;
	isAddingToCart?: boolean;
};

/**
 * Product Controls Component
 *
 * @param {ProductControlsProps} props
 */
export const ProductControls: FunctionComponent<ProductControlsProps> = ({
	onAddToCartClick,
	resetAddToCart,
	addToCartStatus,
	units,
	status,
	isAddingToCart
}) => {
	const { showAsEmployee } = useSiteViewPreference();
	const { currentVariant, selectedVariant } = useContext(VariantContext);
	const { selection, setSelection } = useContext(AddToCartContext);
	const { productFamily } = useContext(ProductFamilyContext);

	const { id } = productFamily;

	// Exit Earlier
	if (!productFamily && !selectedVariant) {
		return null;
	}

	const rawVariationGroups = 'variationGroups' in productFamily ? productFamily.variationGroups : [];

	// Map variation groups to show the correct variant data based on the selected variant
	const variationGroups = mapVariationGroupsToSelectedVariant(rawVariationGroups, selectedVariant.title);

	return (
		<div data-testid="product-controls" id={PRODUCT_CONFIG_SECTION_ID} className="dn-p">
			{variationGroups.length > 0 &&
				variationGroups
					.filter((group) => group.options.length > 0)
					.map(({ name, help, options }, index) => (
						<ProductVariationGroup
							key={name + index}
							name={name}
							helpText={help?.description}
							helpImage={help?.image || undefined}
							options={options}
						/>
					))}
			<ProductConfiguration
				id={id}
				selection={selection}
				configStatus={status}
				resetAddToCart={resetAddToCart}
				addToCartStatus={addToCartStatus}
				changeSelection={setSelection}
			/>

			<div className={'mt2 mt4-ns'}>
				<ShippingEligibility showZipCodeChanger={true} showOrderedBy={true} />
			</div>

			{showAsEmployee && (
				<div className="mt2 dn db-ns">
					<LoadableInternalToolsPanel productShowroomCode={currentVariant.productShowroomCode} />
				</div>
			)}

			{currentVariant.sampleId ? (
				<div className={'mt3 mb2'}>
					<AddSampleToCartButton sampleId={currentVariant.sampleId} pageName={ANALYTICS_PAGE_NAMES.PRODUCT_DETAILS_PAGE} />
				</div>
			) : null}
			{currentVariant.status === 'pending' ? (
				<div className="theme-error f4 mt3 mb3">
					This product is pending addition to the site. Please reach out to the brand manager for more info.
				</div>
			) : (
				<>
					<AddToCartSection onAddToCartClick={onAddToCartClick} units={units} status={status} isAddingToCart={isAddingToCart} />
					<ExtraControlsWidget />
				</>
			)}
		</div>
	);
};
